import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import {
  ArrowLeft, ArrowRight, CheckCircle2, Lock, Sparkles, Share2, Copy, Check,
  Code2, Database, GitBranch, Type, Box, Layers, RefreshCw, Wand2, AlertTriangle,
  FolderTree, Package, FlaskConical, ShieldCheck, Cpu, Workflow,
  Globe, Bot, Server, Network, BarChart3, LineChart, Brain, Zap, MessageSquare, Rocket,
  Trophy, BookOpen, Loader2, FileDown, Mail,
  Binary, Container, ShieldAlert, Gauge, Anchor
} from "lucide-react";
import { getStoredUser, getAuthConfig } from "../lib/userAuth";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Per-module icon + accent color (in catalog order). Tuned to feel hand-curated.
const MODULE_THEME = {
  "python-basics":              { icon: Code2,          accent: "#00F0FF" },
  "data-structures":            { icon: Database,       accent: "#00F0FF" },
  "control-flow-functions":     { icon: GitBranch,      accent: "#00F0FF" },
  "strings-text":               { icon: Type,           accent: "#00F0FF" },
  "oop-fundamentals":           { icon: Box,            accent: "#3DDC84" },
  "oop-advanced":               { icon: Layers,         accent: "#3DDC84" },
  "iterators-generators":       { icon: RefreshCw,      accent: "#3DDC84" },
  "decorators-closures":        { icon: Wand2,          accent: "#3DDC84" },
  "errors-logging":             { icon: AlertTriangle,  accent: "#FFC857" },
  "files-filesystem":           { icon: FolderTree,     accent: "#FFC857" },
  "modules-packages":           { icon: Package,        accent: "#FFC857" },
  "testing-pytest":             { icon: FlaskConical,   accent: "#FFC857" },
  "type-hints-mypy":            { icon: ShieldCheck,    accent: "#FFC857" },
  "concurrency-threads":        { icon: Cpu,            accent: "#FF6B6B" },
  "asyncio-deep-dive":          { icon: Workflow,       accent: "#FF6B6B" },
  // Premium / AI track
  "http-networking":            { icon: Globe,          accent: "#7000FF" },
  "web-scraping":               { icon: Bot,            accent: "#7000FF" },
  "apis-fastapi":               { icon: Server,         accent: "#7000FF" },
  "databases-orms":             { icon: Network,        accent: "#7000FF" },
  "data-analysis-pandas":       { icon: BarChart3,      accent: "#7000FF" },
  "visualization":              { icon: LineChart,      accent: "#7000FF" },
  "ml-foundations":             { icon: Brain,          accent: "#FF3DD9" },
  "deep-learning-pytorch":      { icon: Zap,            accent: "#FF3DD9" },
  "llms-prompt-engineering":    { icon: MessageSquare,  accent: "#FF3DD9" },
  "ai-agent-production":        { icon: Rocket,         accent: "#FF3DD9" },
  // Pro+ Expansion (26-30)
  "dsa-algorithms":             { icon: Binary,         accent: "#FFB347" },
  "production-devops":          { icon: Container,      accent: "#FFB347" },
  "security-crypto":            { icon: ShieldAlert,    accent: "#FFB347" },
  "performance-optimization":   { icon: Gauge,          accent: "#FFB347" },
  "django-framework":           { icon: Anchor,         accent: "#FFB347" },
};

function getTheme(slug) {
  return MODULE_THEME[slug] || { icon: BookOpen, accent: "#A0A0B8" };
}

function PdfCaptureBlock({ user }) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState(user?.email || "");
  const [name, setName] = useState(user?.name || "");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError("Please enter a valid email.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/python-lab/curriculum-pdf-request`, { email, name: name || null });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Could not send. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="mt-8 max-w-md mx-auto bg-emerald-500/10 border border-emerald-500/30 rounded-xl px-5 py-4 flex items-start gap-3 text-left"
        data-testid="pdf-capture-success"
      >
        <div className="w-9 h-9 rounded-full bg-emerald-500/20 flex items-center justify-center shrink-0">
          <CheckCircle2 size={18} className="text-emerald-400" />
        </div>
        <div>
          <p className="font-outfit font-bold text-white text-sm">Check your inbox</p>
          <p className="text-zinc-400 text-xs font-dm-sans mt-0.5 leading-relaxed">
            The 27-page curriculum PDF is on its way to <span className="text-white">{email}</span>.
            Forward it to your team or hiring manager — it doubles as a skills roadmap for any Python or AI role.
          </p>
        </div>
      </motion.div>
    );
  }

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        data-testid="pdf-capture-open-btn"
        className="mt-5 inline-flex items-center gap-2 bg-zinc-900/70 hover:bg-zinc-900 border border-zinc-700 hover:border-[#00F0FF]/40 text-zinc-200 hover:text-white text-xs font-dm-sans font-bold px-4 py-2 rounded-full transition-colors"
      >
        <FileDown size={14} className="text-[#00F0FF]" />
        Get the curriculum PDF
      </button>
    );
  }

  return (
    <motion.form
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
      onSubmit={submit}
      className="mt-6 max-w-lg mx-auto bg-zinc-900/70 backdrop-blur-sm border border-zinc-800 rounded-2xl p-5"
      data-testid="pdf-capture-form"
    >
      <div className="flex items-center gap-2 mb-1.5">
        <FileDown size={14} className="text-[#00F0FF]" />
        <span className="font-outfit font-bold text-white text-sm">Get the full curriculum PDF</span>
      </div>
      <p className="text-zinc-500 text-xs font-dm-sans mb-4">
        32-page brochure (1 cover + 30 module pages + completion certificate teaser).
        Delivered straight to your inbox.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-2">
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Your name (optional)"
          maxLength={80}
          data-testid="pdf-capture-name"
          className="bg-zinc-950 border border-zinc-800 focus:border-[#00F0FF]/50 outline-none rounded-lg px-3 py-2 text-sm text-white placeholder-zinc-600 font-dm-sans transition-colors"
        />
        <div className="relative">
          <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600 pointer-events-none" />
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@company.com"
            required
            data-testid="pdf-capture-email"
            className="w-full bg-zinc-950 border border-zinc-800 focus:border-[#00F0FF]/50 outline-none rounded-lg pl-9 pr-3 py-2 text-sm text-white placeholder-zinc-600 font-dm-sans transition-colors"
          />
        </div>
      </div>
      <button
        type="submit"
        disabled={submitting}
        data-testid="pdf-capture-submit"
        className="w-full inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#00F0FF] to-[#7000FF] text-black font-outfit font-black py-2.5 rounded-lg disabled:opacity-60 disabled:cursor-wait hover:brightness-110 transition-all"
      >
        {submitting ? (
          <><Loader2 size={14} className="animate-spin" /> Sending…</>
        ) : (
          <>Email me the curriculum <ArrowRight size={14} /></>
        )}
      </button>
      {error && (
        <p className="text-red-400 text-xs font-dm-sans text-center mt-2.5" data-testid="pdf-capture-error">
          {error}
        </p>
      )}
      <p className="text-zinc-600 text-[10px] font-dm-sans text-center mt-2.5 leading-relaxed">
        We'll only email you about ShansoAI Python Lab. No spam. Unsubscribe anytime.
      </p>
    </motion.form>
  );
}

function ChapterDivider({ label, sublabel }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.5 }}
      className="relative flex flex-col items-center text-center my-8"
      data-testid={`roadmap-chapter-${label.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <div className="absolute left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-zinc-700 to-transparent" />
      <div className="relative bg-[#050505] px-6 py-3 z-10">
        <span className="text-[10px] font-mono font-bold tracking-[0.3em] text-zinc-500 uppercase">
          {label}
        </span>
        <p className="text-zinc-400 text-sm font-dm-sans mt-1 max-w-md">{sublabel}</p>
      </div>
    </motion.div>
  );
}

function MilestoneNode({ module, side, progress, isCurrent, onOpen, locked }) {
  const { icon: Icon, accent } = getTheme(module.slug);
  const pct = progress?.progress_pct || 0;
  const completed = progress?.lessons_completed || 0;
  const total = module.lesson_count || progress?.lessons_total || 20;
  const done = pct >= 100 && total > 0;

  // Mobile: always full-width single column. Desktop: alternating sides.
  const isLeft = side === "left";

  return (
    <motion.div
      initial={{ opacity: 0, x: isLeft ? -40 : 40 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: 0.45 }}
      className="relative grid grid-cols-1 lg:grid-cols-[1fr_auto_1fr] items-center gap-4 lg:gap-8"
      data-testid={`roadmap-module-${module.slug}`}
    >
      {/* Left card slot */}
      <div className={`${isLeft ? "lg:block lg:text-right lg:pr-2" : "hidden lg:block"}`}>
        {isLeft && (
          <ModuleCard module={module} progress={progress} isCurrent={isCurrent}
                      done={done} pct={pct} completed={completed} total={total}
                      onOpen={onOpen} locked={locked} side="left" />
        )}
      </div>

      {/* Center node */}
      <div className="flex justify-center relative z-10">
        <div
          className={`relative w-16 h-16 rounded-2xl flex items-center justify-center border-2 transition-all ${
            done ? "bg-emerald-500/15 border-emerald-400" :
            isCurrent ? "bg-zinc-900 border-[#00F0FF] shadow-[0_0_28px_rgba(0,240,255,0.45)]" :
            locked ? "bg-zinc-900/40 border-zinc-800" :
            "bg-zinc-900 border-zinc-700"
          }`}
          style={!done && !isCurrent && !locked ? { borderColor: accent + "60" } : {}}
        >
          {done ? (
            <CheckCircle2 size={26} className="text-emerald-400" />
          ) : (
            <Icon size={24} style={{ color: locked ? "#52525b" : accent }} />
          )}
          {/* Order pip */}
          <div className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-[#050505] border border-zinc-700 flex items-center justify-center text-[10px] font-mono font-bold text-zinc-300">
            {String(module.order).padStart(2, "0")}
          </div>
          {isCurrent && !done && (
            <motion.div
              animate={{ scale: [1, 1.4, 1], opacity: [0.55, 0, 0.55] }}
              transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
              className="absolute inset-0 rounded-2xl bg-[#00F0FF]/30 pointer-events-none"
            />
          )}
        </div>

        {/* "You are here" badge (desktop only — sits beneath node) */}
        {isCurrent && (
          <div className="absolute top-full mt-2 left-1/2 -translate-x-1/2 whitespace-nowrap text-[9px] font-mono font-bold text-[#00F0FF] uppercase tracking-widest"
               data-testid="roadmap-you-are-here">
            ▲ You are here
          </div>
        )}
      </div>

      {/* Right card slot */}
      <div className={`${!isLeft ? "lg:block lg:pl-2" : "hidden lg:block"}`}>
        {!isLeft && (
          <ModuleCard module={module} progress={progress} isCurrent={isCurrent}
                      done={done} pct={pct} completed={completed} total={total}
                      onOpen={onOpen} locked={locked} side="right" />
        )}
      </div>

      {/* Mobile fallback: card always shows below the node */}
      <div className="lg:hidden col-span-1">
        <ModuleCard module={module} progress={progress} isCurrent={isCurrent}
                    done={done} pct={pct} completed={completed} total={total}
                    onOpen={onOpen} locked={locked} side="mobile" />
      </div>
    </motion.div>
  );
}

function ModuleCard({ module, done, pct, completed, total, isCurrent, onOpen, locked, side }) {
  const { accent } = getTheme(module.slug);
  const align = side === "left" ? "lg:text-right lg:items-end" : "lg:text-left lg:items-start";
  return (
    <button
      onClick={onOpen}
      data-testid={`roadmap-card-${module.slug}`}
      className={`w-full flex flex-col ${align} group cursor-pointer`}
    >
      <div
        className={`w-full bg-zinc-900/60 backdrop-blur-sm border rounded-2xl p-5 transition-all hover:border-zinc-600 ${
          done ? "border-emerald-500/30" :
          isCurrent ? "border-[#00F0FF]/50 shadow-[0_0_30px_rgba(0,240,255,0.18)]" :
          locked ? "border-zinc-800/70 opacity-70" : "border-zinc-800"
        }`}
      >
        <div className={`flex items-center gap-2 mb-2 flex-wrap ${side === "left" ? "lg:justify-end" : ""}`}>
          <span className="text-[10px] font-mono font-bold tracking-wider"
                style={{ color: accent }}>
            MODULE {String(module.order).padStart(2, "0")}
          </span>
          {module.is_premium && (
            <span className="inline-flex items-center gap-1 text-[9px] font-bold text-amber-300 bg-amber-300/10 border border-amber-300/30 px-1.5 py-0.5 rounded">
              <Lock size={9} /> PREMIUM
            </span>
          )}
          {done && (
            <span className="inline-flex items-center gap-1 text-[9px] font-mono font-bold text-emerald-400 bg-emerald-400/10 border border-emerald-400/30 px-1.5 py-0.5 rounded">
              <CheckCircle2 size={9} /> COMPLETED
            </span>
          )}
        </div>
        <h3 className="font-outfit font-bold text-white text-lg mb-1.5">{module.title}</h3>
        <p className={`text-xs text-zinc-400 font-dm-sans leading-relaxed ${side === "left" ? "lg:text-right" : ""}`}>
          {module.summary}
        </p>

        {/* Progress bar (always rendered; muted when no progress) */}
        <div className="mt-4">
          <div className={`flex items-center justify-between text-[10px] font-dm-sans text-zinc-500 mb-1.5 ${side === "left" ? "lg:flex-row-reverse" : ""}`}>
            <span>{completed}/{total} lessons</span>
            <span className="font-bold text-zinc-300">{pct}%</span>
          </div>
          <div className="w-full h-1 bg-zinc-800 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-700"
              style={{
                width: `${Math.max(pct, total === 0 ? 0 : 2)}%`,
                background: done
                  ? "linear-gradient(90deg, #3DDC84, #00F0FF)"
                  : `linear-gradient(90deg, ${accent}, ${accent}aa)`,
              }}
            />
          </div>
        </div>
      </div>
    </button>
  );
}

export default function RoadmapPage() {
  const navigate = useNavigate();
  const user = getStoredUser();
  const [modules, setModules] = useState([]);
  const [progressByMod, setProgressByMod] = useState({});
  const [continueSlug, setContinueSlug] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [modsRes, progRes, contRes] = await Promise.all([
          axios.get(`${API}/python-lab/modules`),
          user?.session_token
            ? axios.get(`${API}/python-lab/me/progress`, getAuthConfig()).catch(() => ({ data: [] }))
            : Promise.resolve({ data: [] }),
          user?.session_token
            ? axios.get(`${API}/python-lab/me/continue`, getAuthConfig()).catch(() => ({ data: null }))
            : Promise.resolve({ data: null }),
        ]);
        if (cancelled) return;

        setModules(modsRes.data || []);
        const map = {};
        (progRes.data || []).forEach((m) => {
          map[m.module_id] = m;
        });
        setProgressByMod(map);

        // Find which module the user is currently on. Continue endpoint returns
        // the next lesson, which has a module_id we can match.
        const cont = contRes.data;
        if (cont?.lesson?.module_id) {
          const mod = (modsRes.data || []).find((m) => m.module_id === cont.lesson.module_id);
          if (mod) setContinueSlug(mod.slug);
        }
      } catch (e) {
        console.error("Roadmap load failed:", e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.session_token]);

  const stats = useMemo(() => {
    const total = modules.length;
    const completedMods = modules.filter(
      (m) => (progressByMod[m.module_id]?.progress_pct || 0) >= 100
    ).length;
    const totalLessons = modules.reduce((s, m) => s + (m.lesson_count || 0), 0);
    const completedLessons = Object.values(progressByMod).reduce(
      (s, p) => s + (p.lessons_completed || 0),
      0
    );
    return { total, completedMods, totalLessons, completedLessons };
  }, [modules, progressByMod]);

  const handleShare = async () => {
    const url = window.location.origin + "/ai-tools/python-lab/roadmap";
    try {
      if (navigator.share) {
        await navigator.share({
          title: "ShansoAI Python Lab — 25-module curriculum",
          text: "Look at everything I'm learning on ShansoAI Python Lab — 30 modules, 600 hands-on Python lessons.",
          url,
        });
        return;
      }
    } catch { /* user cancelled */ }
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch (e) {
      console.error(e);
    }
  };

  // Module groupings: 1-15 generalist, 16-25 AI specialty, 26-30 Pro+ expansion.
  const generalist = modules.filter((m) => m.order >= 1 && m.order <= 15);
  const specialty = modules.filter((m) => m.order >= 16 && m.order <= 25);
  const proPlus = modules.filter((m) => m.order >= 26);

  const sideFor = (idx) => (idx % 2 === 0 ? "right" : "left");

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      {/* Header */}
      <header className="border-b border-zinc-900 sticky top-0 z-30 bg-[#050505]/85 backdrop-blur-xl">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <button
            data-testid="back-to-lab"
            onClick={() => navigate("/ai-tools/python-lab")}
            className="flex items-center gap-2 text-zinc-400 hover:text-white text-sm font-dm-sans transition-colors"
          >
            <ArrowLeft size={16} /> Python Lab
          </button>
          <button
            onClick={handleShare}
            data-testid="roadmap-share-btn"
            className="inline-flex items-center gap-2 bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 text-white text-xs font-dm-sans font-bold px-3.5 py-2 rounded-lg transition-colors"
          >
            {copied ? <Check size={14} className="text-emerald-400" /> : <Share2 size={14} />}
            {copied ? "Link copied" : "Share roadmap"}
          </button>
        </div>
      </header>

      {/* Hero */}
      <section className="relative max-w-6xl mx-auto px-6 pt-16 pb-10 text-center overflow-hidden">
        <div className="absolute -top-16 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-[#00F0FF]/[0.06] rounded-full blur-3xl pointer-events-none" />
        <div className="absolute top-32 right-10 w-[400px] h-[400px] bg-[#7000FF]/[0.08] rounded-full blur-3xl pointer-events-none" />
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="relative z-10"
        >
          <div className="inline-flex items-center gap-2 bg-[#00F0FF]/10 border border-[#00F0FF]/30 rounded-full px-3 py-1 text-xs font-mono text-[#00F0FF] mb-6">
            <Sparkles size={12} /> CURRICULUM ROADMAP
          </div>
          <h1 className="font-outfit font-black text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.05] tracking-tight">
            From <span className="text-zinc-500">print(&quot;Hello&quot;)</span>
            <br />
            to{" "}
            <span className="bg-gradient-to-r from-[#00F0FF] to-[#7000FF] bg-clip-text text-transparent">
              shipping AI agents
            </span>
            .
          </h1>
          <p className="text-zinc-400 text-base sm:text-lg max-w-2xl mx-auto mt-6 font-dm-sans leading-relaxed">
            30 modules. 600 hands-on Python lessons. One progressive journey from
            absolute beginner to production AI engineer — auto-graded, AI-tutored,
            zero installs.
          </p>

          {/* Stat strip */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 max-w-3xl mx-auto mt-10">
            <Stat label="Modules" value={`${stats.completedMods}/${stats.total || 30}`} accent="#00F0FF" />
            <Stat label="Lessons" value={`${stats.completedLessons}/${stats.totalLessons || 600}`} accent="#7000FF" />
            <Stat label="Concept lessons" value={stats.totalLessons ? Math.round(stats.totalLessons * 0.75) : 450} accent="#3DDC84" />
            <Stat label="Mini-projects" value={stats.total || 30} accent="#FFC857" />
          </div>

          {/* Continue CTA */}
          {user?.session_token ? (
            continueSlug ? (
              <button
                onClick={() => navigate(`/ai-tools/python-lab/module/${continueSlug}`)}
                data-testid="roadmap-continue-btn"
                className="mt-10 inline-flex items-center gap-2 bg-[#00F0FF] text-black font-outfit font-bold px-5 py-3 rounded-lg hover:bg-white transition-colors"
              >
                Continue your journey <ArrowRight size={16} />
              </button>
            ) : null
          ) : (
            <button
              onClick={() => navigate("/ai-tools/python-lab")}
              data-testid="roadmap-start-btn"
              className="mt-10 inline-flex items-center gap-2 bg-gradient-to-r from-[#00F0FF] to-[#7000FF] text-black font-outfit font-bold px-5 py-3 rounded-lg hover:brightness-110 transition-all"
            >
              Start your journey — free <ArrowRight size={16} />
            </button>
          )}

          {/* Lead-capture: curriculum PDF */}
          <PdfCaptureBlock user={user} />
        </motion.div>
      </section>

      {/* Roadmap timeline */}
      <main className="relative max-w-6xl mx-auto px-6 py-12">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 size={28} className="text-[#00F0FF] animate-spin" />
          </div>
        ) : (
          <div className="relative">
            {/* Glowing center spine — desktop only */}
            <div className="hidden lg:block absolute top-0 bottom-0 left-1/2 -translate-x-px w-px bg-gradient-to-b from-transparent via-[#00F0FF]/20 to-transparent" />
            <div className="hidden lg:block absolute top-0 bottom-0 left-1/2 -translate-x-px w-px bg-gradient-to-b from-[#00F0FF]/0 via-[#7000FF]/30 to-transparent"
                 style={{ top: "55%" }} />

            {/* Mobile spine */}
            <div className="lg:hidden absolute top-0 bottom-0 left-8 w-px bg-gradient-to-b from-[#00F0FF]/30 via-[#7000FF]/30 to-transparent" />

            <ChapterDivider
              label="Foundations"
              sublabel="Modules 1–15 · Build a rock-solid Python engineer's toolkit."
            />

            <div className="space-y-12">
              {generalist.map((m, idx) => (
                <MilestoneNode
                  key={m.module_id}
                  module={m}
                  side={sideFor(idx)}
                  progress={progressByMod[m.module_id]}
                  isCurrent={continueSlug === m.slug}
                  locked={false /* visual roadmap shouldn't gray things out */}
                  onOpen={() => navigate(`/ai-tools/python-lab/module/${m.slug}`)}
                />
              ))}
            </div>

            <ChapterDivider
              label="AI Specialty"
              sublabel="Modules 16–25 · Pivot into ML, LLMs and production AI engineering."
            />

            <div className="space-y-12">
              {specialty.map((m, idx) => (
                <MilestoneNode
                  key={m.module_id}
                  module={m}
                  side={sideFor(idx + generalist.length)}
                  progress={progressByMod[m.module_id]}
                  isCurrent={continueSlug === m.slug}
                  locked={false}
                  onOpen={() => navigate(`/ai-tools/python-lab/module/${m.slug}`)}
                />
              ))}
            </div>

            {proPlus.length > 0 && (
              <>
                <ChapterDivider
                  label="Pro+ Expansion"
                  sublabel="Modules 26–30 · DSA, DevOps, Security, Performance, Django — the topics seniors are paid for."
                />
                <div className="space-y-12">
                  {proPlus.map((m, idx) => (
                    <MilestoneNode
                      key={m.module_id}
                      module={m}
                      side={sideFor(idx + generalist.length + specialty.length)}
                      progress={progressByMod[m.module_id]}
                      isCurrent={continueSlug === m.slug}
                      locked={false}
                      onOpen={() => navigate(`/ai-tools/python-lab/module/${m.slug}`)}
                    />
                  ))}
                </div>
              </>
            )}

            {/* Final destination node */}
            <div className="relative flex justify-center mt-16">
              <motion.div
                initial={{ opacity: 0, scale: 0.85 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="text-center max-w-md"
              >
                <div className="w-20 h-20 mx-auto rounded-2xl bg-gradient-to-br from-[#00F0FF] to-[#7000FF] flex items-center justify-center mb-5 shadow-[0_0_50px_rgba(0,240,255,0.3)]">
                  <Trophy size={32} className="text-black" />
                </div>
                <h2 className="font-outfit font-black text-2xl text-white mb-2">
                  Production Python Engineer
                </h2>
                <p className="text-zinc-400 text-sm font-dm-sans mb-6 leading-relaxed">
                  500 lessons in. You can ship a multi-agent AI app end-to-end —
                  with tests, types, deployment and observability.
                </p>
                {user?.session_token ? (
                  <button
                    onClick={() => navigate("/ai-tools/python-lab")}
                    className="inline-flex items-center gap-2 bg-zinc-900 border border-zinc-700 hover:border-[#00F0FF]/50 text-white text-sm font-dm-sans font-bold px-4 py-2.5 rounded-lg transition-colors"
                  >
                    Back to Python Lab <ArrowRight size={14} />
                  </button>
                ) : (
                  <button
                    onClick={() => navigate("/ai-tools/python-lab")}
                    className="inline-flex items-center gap-2 bg-gradient-to-r from-[#00F0FF] to-[#7000FF] text-black text-sm font-dm-sans font-black px-5 py-3 rounded-lg hover:brightness-110 transition-all"
                  >
                    Start the journey for free <ArrowRight size={14} />
                  </button>
                )}
              </motion.div>
            </div>
          </div>
        )}
      </main>

      {/* Footer band */}
      <section className="border-t border-zinc-900 bg-zinc-950/40 mt-16">
        <div className="max-w-6xl mx-auto px-6 py-10 text-center">
          <p className="text-zinc-500 text-sm font-dm-sans">
            Curriculum maintained by ShansoAI Hub. Lessons authored with Claude Sonnet 4.5,
            executed in your browser via Pyodide. Zero installs.
          </p>
        </div>
      </section>
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl px-4 py-3 backdrop-blur-sm">
      <div className="font-outfit font-black text-2xl" style={{ color: accent }}>
        {value}
      </div>
      <div className="text-[10px] font-mono font-bold tracking-wider text-zinc-500 uppercase mt-0.5">
        {label}
      </div>
    </div>
  );
}
